/**
 * Feedback de teclado telefónico (Web Audio API).
 * Tonos DTMF estándar con envolvente corta y suave (tipo iPhone: “tap” limpio, sin chirrido largo).
 * Requiere gesto de usuario: usar `primeDtmfAudio()` al abrir el marcador / pointerdown en el teclado.
 */

const DTMF_HZ: Record<string, [number, number]> = {
  '1': [697, 1209],
  '2': [697, 1336],
  '3': [697, 1477],
  '4': [770, 1209],
  '5': [770, 1336],
  '6': [770, 1477],
  '7': [852, 1209],
  '8': [852, 1336],
  '9': [852, 1477],
  '*': [941, 1209],
  '0': [941, 1336],
  '#': [941, 1477],
};

/** Curva de ganancia reutilizable (medio seno = campana suave, sin clicks). */
let cachedBellCurve: Float32Array | null = null;
let cachedBellPeak = -1;

function getBellGainCurve(peak: number): Float32Array {
  const sampleCount = 72;
  if (cachedBellCurve && cachedBellPeak === peak) {
    return cachedBellCurve;
  }
  const curve = new Float32Array(sampleCount);
  for (let i = 0; i < sampleCount; i++) {
    const t = sampleCount > 1 ? i / (sampleCount - 1) : 0;
    /** sin(πt): 0 → 1 → 0; suena más “redondo” que rampas exponenciales tipo pitido. */
    curve[i] = peak * Math.sin(Math.PI * t);
  }
  cachedBellCurve = curve;
  cachedBellPeak = peak;
  return curve;
}

let sharedCtx: AudioContext | null = null;

/**
 * Contexto **solo** para timbrado sintético. Se crea en la cadena de `getUserMedia` (conexión WebRTC),
 * no al pulsar el teclado DTMF — evita que un `AudioContext` “contaminado” o siempre `suspended` rompa el “tuuu” en auto-llamada.
 */
let ringbackOnlyCtx: AudioContext | null = null;

function kickCtxWithInaudiblePulse(ctx: AudioContext): void {
  try {
    const t0 = ctx.currentTime + 0.002;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.00001, t0);
    g.gain.linearRampToValueAtTime(0.00008, t0 + 0.012);
    g.gain.linearRampToValueAtTime(0.00001, t0 + 0.028);
    const osc = ctx.createOscillator();
    osc.frequency.value = 440;
    osc.connect(g);
    g.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.032);
  } catch {
    // ignore
  }
}

/**
 * Llamar solo desde `connect()` justo después de `getUserMedia` (antes de parar el stream).
 */
export function createRingbackAudioContextFromMicrophoneGesture(): void {
  if (typeof window === 'undefined') return;
  try {
    if (ringbackOnlyCtx && ringbackOnlyCtx.state !== 'closed') {
      void ringbackOnlyCtx.resume();
      kickCtxWithInaudiblePulse(ringbackOnlyCtx);
      return;
    }
    ringbackOnlyCtx = new AudioContext({ latencyHint: 'interactive' });
    void ringbackOnlyCtx.resume();
    kickCtxWithInaudiblePulse(ringbackOnlyCtx);
  } catch {
    ringbackOnlyCtx = null;
  }
}

export function getRingbackOnlyAudioContext(): AudioContext | null {
  return ringbackOnlyCtx;
}

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!sharedCtx) {
    try {
      sharedCtx = new AudioContext({ latencyHint: 'interactive' });
    } catch {
      return null;
    }
  }
  return sharedCtx;
}

/**
 * Desbloquea el AudioContext en un gesto del usuario (abrir marcador, primer toque, etc.).
 */
export function primeDtmfAudio(): void {
  const ctx = getAudioContext();
  if (!ctx || ctx.state === 'closed') return;
  void ctx.resume();
}

/**
 * Tras `getUserMedia({ audio })` (mismo flujo que conecta WebRTC), algunos navegadores permiten
 * “despertar” el `AudioContext` en esa cadena; el timbrado sintético de auto-llamada no pasa por el clic en “Llamar”.
 */
export function unlockWebAudioAfterMicrophoneGranted(): void {
  const ctx = getAudioContext();
  if (!ctx || ctx.state === 'closed') return;
  void ctx.resume();
  kickCtxWithInaudiblePulse(ctx);
}

/**
 * Varios `resume()` espaciados (Realtime / pestaña en segundo plano).
 */
export async function resumeAudioContextWithRetries(
  ctx: AudioContext | null | undefined,
  maxAttempts = 8,
  delayMs = 40,
): Promise<boolean> {
  if (!ctx || ctx.state === 'closed') return false;
  for (let i = 0; i < maxAttempts; i++) {
    if (ctx.state === 'running') return true;
    try {
      await ctx.resume();
    } catch {
      break;
    }
    // `resume()` puede haber cambiado el estado; TS no lo modela y mantiene el
    // estrechamiento previo ('suspended'), por eso re-ensanchamos al leerlo.
    if ((ctx.state as AudioContextState) === 'running') return true;
    await new Promise((r) => setTimeout(r, delayMs));
  }
  return ctx.state === 'running';
}

export async function resumeSharedAudioContextWithRetries(
  maxAttempts = 8,
  delayMs = 40,
): Promise<boolean> {
  return resumeAudioContextWithRetries(getAudioContext(), maxAttempts, delayMs);
}

/** Prioriza el contexto de timbrado (micrófono), luego el compartido con DTMF. */
export async function resumeAllContextsForSyntheticRingback(
  maxAttempts = 10,
  delayMs = 40,
): Promise<boolean> {
  const r = getRingbackOnlyAudioContext();
  if (await resumeAudioContextWithRetries(r, maxAttempts, delayMs)) return true;
  return resumeAudioContextWithRetries(getAudioContext(), maxAttempts, delayMs);
}

/** Mismo AudioContext que DTMF (tono de fin de llamada, etc.). */
export function getSharedAudioContext(): AudioContext | null {
  return getAudioContext();
}

export interface PlayDtmfOptions {
  /** Duración total del “tap” en ms (iOS ~60–75). */
  durationMs?: number;
  /** Pico de volumen 0–1 (dos osciladores; mantener moderado). */
  gain?: number;
}

/**
 * Reproduce el tono de una tecla (1–9, *, 0, #).
 * Programación síncrona en el mismo tick que el clic (sin `resume().then`) para mínimo desfase.
 */
export function playDtmfKey(digit: string, options?: PlayDtmfOptions): void {
  const freqs = DTMF_HZ[digit];
  if (!freqs) return;

  const durationMs = options?.durationMs ?? 68;
  const peakGain = options?.gain ?? 0.078;
  const ctx = getAudioContext();
  if (!ctx) return;

  const durSec = Math.max(0.035, durationMs / 1000);

  if (ctx.state !== 'closed') {
    void ctx.resume();
  }

  /**
   * Casi inmediato: en el mismo frame del pointerdown, `currentTime` basta.
   * (Evita +2 ms fijos que suman sensación de lag en teclas rápidas.)
   */
  const t0 = ctx.currentTime + 0.0005;

  const master = ctx.createGain();
  const curve = getBellGainCurve(peakGain);
  try {
    master.gain.setValueCurveAtTime(curve, t0, durSec);
  } catch {
    /** Fallback si setValueCurveAtTime fallara en algún navegador antiguo. */
    master.gain.setValueAtTime(0.0001, t0);
    master.gain.exponentialRampToValueAtTime(peakGain, t0 + 0.004);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + durSec);
  }

  const makeOsc = (freq: number) => {
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(freq, t0);
    o.connect(master);
    o.start(t0);
    o.stop(t0 + durSec + 0.015);
  };

  makeOsc(freqs[0]);
  makeOsc(freqs[1]);
  master.connect(ctx.destination);
}
